/**
 * Dashboard helpers — Hebrew date / relative-time formatting + activity-
 * kind labels. Kept as plain functions (no React) so the per-card
 * components stay thin.
 */
const HE_DAYS = ['יום ראשון', 'יום שני', 'יום שלישי', 'יום רביעי', 'יום חמישי', 'יום שישי', 'יום שבת'];
const HE_MONTHS = [
    'ינואר', 'פברואר', 'מרץ', 'אפריל', 'מאי', 'יוני',
    'יולי', 'אוגוסט', 'ספטמבר', 'אוקטובר', 'נובמבר', 'דצמבר',
];
export function formatHebrewDate(d = new Date()) {
    return `${HE_DAYS[d.getDay()]}, ${d.getDate()} ב${HE_MONTHS[d.getMonth()]} ${d.getFullYear()}`;
}
/**
 * Compact Hebrew relative time: "עכשיו", "לפני 3 דקות", "לפני שעתיים",
 * "לפני 4 שעות", "אתמול", "לפני 3 ימים", "לפני 2 שבועות", "לפני חודש".
 *
 * Takes an ISO-8601 string (what the server returns). Returns a short
 * phrase without exact numerals when a dedicated word exists (שעתיים
 * for 2 hours, יומיים for 2 days, שבועיים for 2 weeks) — the Hebrew
 * dual is the natural reading.
 */
export function formatRelativeTimeHe(iso) {
    if (!iso)
        return '—';
    const then = new Date(iso).getTime();
    if (!Number.isFinite(then))
        return '—';
    const diffS = Math.max(0, Math.floor((Date.now() - then) / 1000));
    if (diffS < 60)
        return 'עכשיו';
    const m = Math.floor(diffS / 60);
    if (m < 60)
        return m === 1 ? 'לפני דקה' : `לפני ${m} דקות`;
    const h = Math.floor(m / 60);
    if (h < 24) {
        if (h === 1)
            return 'לפני שעה';
        if (h === 2)
            return 'לפני שעתיים';
        return `לפני ${h} שעות`;
    }
    const d = Math.floor(h / 24);
    if (d === 1)
        return 'אתמול';
    if (d === 2)
        return 'שלשום';
    if (d < 7)
        return `לפני ${d} ימים`;
    const w = Math.floor(d / 7);
    if (w === 1)
        return 'לפני שבוע';
    if (w === 2)
        return 'לפני שבועיים';
    if (w < 5)
        return `לפני ${w} שבועות`;
    const mo = Math.floor(d / 30);
    if (mo === 1)
        return 'לפני חודש';
    if (mo === 2)
        return 'לפני חודשיים';
    if (mo < 12)
        return `לפני ${mo} חודשים`;
    const y = Math.floor(d / 365);
    if (y === 1)
        return 'לפני שנה';
    return `לפני ${y} שנים`;
}
export function kindLabelHe(k) {
    switch (k) {
        case 'app_open': return 'נכנס/ה לאפליקציה';
        case 'clip_view': return 'צפייה בקליפ';
        case 'attempt': return 'ניסיון במבחן';
        case 'passed': return 'עבר/ה מבחן';
        case 'progress': return 'התקדמות בשיעור';
        default: return 'אין פעילות';
    }
}
/**
 * Agorot (1/100 of a shekel — the server's amount_paid unit) → Hebrew-
 * locale shekel string with exactly two fraction digits. e.g. 13930 →
 * "139.30", 10000 → "100.00". Used by every money column that reads a
 * `*_agorot` or `amount_paid` field.
 */
export function formatAgorotHe(agorot) {
    return (agorot / 100).toLocaleString('he-IL', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    });
}
/** First grapheme of a display name, used for avatar fallback. Returns
 *  '?' when the name is empty. Hebrew letters are single-codepoint and
 *  `[...str][0]` safely extracts the first visual character. */
export function initial(name) {
    const s = (name ?? '').trim();
    if (!s)
        return '?';
    return [...s][0] ?? '?';
}
/** Palette consumed by charts + KPI chips. Matches the existing
 *  :root tokens in theme/global.css + the mobile palette. */
export const PALETTE = {
    purple: '#8B2FD6',
    purpleDeep: '#6B21A8',
    lavender: '#F1EAFF',
    lavAlt: '#EFE9FA',
    mint: '#4CE0A8',
    mintSoft: '#E7FBF2',
    amber: '#F2B544',
    amberSoft: '#FFF4D6',
    sky: '#4C8DE0',
    skySoft: '#DEEEFB',
    ink: '#22174B',
    inkMuted: '#6D6386',
    gridLine: 'rgba(107, 33, 168, 0.08)',
};
/** Format a short "HH" tick for the hourly chart. */
export const hourTick = (h) => `${String(h).padStart(2, '0')}:00`;
/** Format a short MM-DD tick for the 30-day charts. Accepts the
 *  YYYY-MM-DD strings the server returns. */
export const shortDay = (iso) => iso.slice(5); // MM-DD
